/**
 * MFASetupPage - 4-step MFA setup (intro → QR → verify → backup codes)
 * MODULE: core/auth/frontend/pages/MFASetupPage.tsx
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'flowbite-react';
import { OTPInput } from '../components/OTPInput';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useMfa } from '../hooks/useMfa';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;
const getToken = () => localStorage.getItem('access_token');

export function MFASetupPage() {
  const navigate = useNavigate();
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('access_token');
  const mfa = useMfa();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [otpValue, setOtpValue] = useState('');
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSkipModal, setShowSkipModal] = useState(false);

  const handleStartSetup = useCallback(async () => {
    setError(null);
    try {
      const data = await mfa.setupMfa();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setBackupCodes(data.backupCodes);
      setStep(2);
    } catch (e) {
      setError('Failed to start setup. Please try again.');
    }
  }, [mfa]);

  const handleVerify = useCallback(async (code: string) => {
    if (!code || code.length !== 6) return;
    setError(null);
    try {
      const success = await mfa.verifySetup(code);
      if (success) setStep(4);
      else setError('Invalid code. Please try again.');
    } catch {
      setError('Verification failed. Please try again.');
    }
  }, [mfa]);

  const downloadBackupCodes = useCallback(() => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'brazn-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [backupCodes]);

  const handleSkip = useCallback(async () => {
    setShowSkipModal(false);
    try {
      const res = await fetch(`${API_BASE}/onboarding/skip-mfa`, {
        method: 'POST',
        headers: { Authorization: getToken() ? `Bearer ${getToken()}` : '' },
      });
      if (res.ok) navigate('/welcome');
    } catch {
      setError('Could not skip. Try again.');
    }
  }, [navigate]);

  if (!hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full mx-auto p-6 text-center">
          <p className="text-gray-600 mb-4">Please log in to continue setting up 2FA.</p>
          <Button color="blue" onClick={() => navigate('/login')}>Go to login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md w-full mx-auto p-6">
        <p className="text-sm text-gray-500 mb-2">3 of 4</p>

        {step === 1 && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Secure your account with 2FA</h2>
            <ul className="text-sm text-gray-600 space-y-2 mt-4 list-disc list-inside">
              <li>Protects against password theft</li>
              <li>Required for sensitive operations</li>
              <li>Uses your phone as a second factor</li>
            </ul>
            <div className="flex gap-3 mt-6">
              <Button color="blue" onClick={handleStartSetup} disabled={mfa.isSettingUp}>
                {mfa.isSettingUp ? <><Spinner size="sm" className="mr-2" /> Starting...</> : 'Set up now'}
              </Button>
              <Button color="gray" onClick={() => setShowSkipModal(true)}>Skip for now</Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Scan the QR code</h2>
            <p className="text-sm text-gray-600 mt-1">Use your authenticator app to scan this code.</p>
            {qrCode && (
              <div className="flex justify-center my-4">
                <img src={`data:image/png;base64,${qrCode}`} alt="QR code" className="max-w-48 h-auto" />
              </div>
            )}
            {secret && (
              <p className="text-xs text-gray-500 font-mono break-all mt-2">Can&apos;t scan? Enter manually: {secret}</p>
            )}
            <Button color="blue" className="mt-4 w-full" onClick={() => setStep(3)}>I&apos;ve scanned the code</Button>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Enter the 6-digit code</h2>
            <p className="text-sm text-gray-600 mt-1">Enter the code from your authenticator app.</p>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            <div className="my-4 flex justify-center">
              <OTPInput
                length={6}
                value={otpValue}
                onChange={setOtpValue}
                onComplete={handleVerify}
                error={!!error}
              />
            </div>
            <Button color="blue" className="w-full" onClick={() => handleVerify(otpValue)} disabled={otpValue.length !== 6}>
              Verify
            </Button>
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Save your backup codes</h2>
            <p className="text-sm text-gray-600 mt-1">Store these codes safely. You can use them if you lose access to your authenticator.</p>
            <div className="my-4 grid grid-cols-2 gap-2 font-mono text-sm bg-gray-100 p-3 rounded">
              {backupCodes.map((c, i) => (
                <span key={i}>{c}</span>
              ))}
            </div>
            <Button color="gray" className="w-full mb-2" onClick={downloadBackupCodes}>Download codes</Button>
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={backupCodesSaved}
                onChange={(e) => setBackupCodesSaved(e.target.checked)}
              />
              <span className="text-sm text-gray-600">I have saved my backup codes</span>
            </label>
            <Button
              color="blue"
              className="w-full mt-4"
              disabled={!backupCodesSaved}
              onClick={() => navigate('/welcome')}
            >
              Continue
            </Button>
          </>
        )}
      </Card>

      <Modal show={showSkipModal} onClose={() => setShowSkipModal(false)} size="md">
        <ModalHeader>Skip 2FA?</ModalHeader>
        <ModalBody>
          <p className="text-gray-600">Your account will be less secure without 2FA. Are you sure you want to skip?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={() => setShowSkipModal(false)}>Set up 2FA</Button>
          <Button color="failure" onClick={handleSkip}>Skip anyway</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
